import { useState, useMemo, useEffect } from "react";
import { useAppData } from "@/data/AppDataContext";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Search, Plus, Megaphone, Globe, DollarSign, Briefcase, TrendingUp,
  Target, Users, Eye, BarChart3, CheckCircle, ArrowUpDown, ArrowUp,
  ArrowDown, Percent, Zap, Handshake, Radio, Link2, Download,
  AlertTriangle, Receipt, FileText, Settings2,
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { StatusBadge } from "@/components/StatusBadge";
import { GROWTH_CHANNELS, DEFAULT_SOURCE_CHANNEL_MAP, suggestChannel, getChannelById, getSelectableChannels } from "@/lib/growthChannelMapping";
import { rankSource, getBudgetRecommendation, type SourceRank } from "@/lib/sourceIntelligence";
import { getJobDisplayValue } from "@/data/jobPricingSelectors";
import {
  buildPayloadContactRequirementUpdates,
  getPayloadContactRequirements,
  getRequiredPayloadFields,
  REQUIRED_PAYLOAD_FIELD_OPTIONS,
  toggleRequiredPayloadField,
} from "@/lib/leadSourcePayloadRequirements";
import {
  BLACKLIST_EMAIL_FILTER_ENABLED_WARNING,
  BLACKLIST_EMAIL_FILTER_UNAVAILABLE_EXPLANATION,
  BLACKLIST_EMAIL_FILTER_UNAVAILABLE_STATUS,
  getBlacklistAllianceConfig,
  getDefaultBlacklistAllianceServiceConfig,
  updateBlacklistAllianceCheck,
  updateBlacklistAllianceEmailFilterFromUi,
  type BlacklistAllianceCheckKey,
} from "@/lib/leadSourceBlacklistAllianceConfig";
import { LeadSourceService as LeadSourceAnalyticsService } from "@/services/lead-source.service";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";
import type { DbLeadSourceRow } from "@/types/database";

/* ═══ Types ═══ */
type SourceType = "aggregator" | "paid_ads" | "referral" | "partner" | "organic" | "other";

interface LeadSource {
  id: string;
  name: string;
  sourceType: SourceType;
  active: boolean;
  campaign: string;
  notes: string;
  // Funnel tracking
  totalLeads: number;
  contacted: number;
  appointmentsSet: number;
  appointmentsCompleted: number;
  // Cost tracking
  monthlyCost?: number;
  // Growth Channel mapping
  growthChannelId?: string;
}

type LeadSourceConfigForm = {
  duplicateWindowDays: string;
  requireTcpa: boolean;
  allowedZipCodes: string;
  marketingBudgetPeriod: "weekly" | "monthly";
  marketingBudgetAmount: string;
  campaignScheduleEnabled: boolean;
  campaignScheduleTimezone: string;
  campaignScheduleDays: string[];
  campaignScheduleStartTime: string;
  campaignScheduleEndTime: string;
};

type ResponseAccessToken = {
  label?: string;
  name?: string;
  id?: string;
  secret?: string;
  scopes?: string[];
} & Record<string, unknown>;

type ProjectTypeRuleDraft = {
  id: string;
  projectType: string;
  active: boolean;
  minBudget: string;
  maxBudget: string;
  minLeadCost: string;
  maxLeadCost: string;
  marketingBudgetPeriod: "weekly" | "monthly";
  marketingBudgetAmount: string;
  allowedZipCodes: string;
};

type NewLeadSourceDraft = Partial<LeadSource> & {
  code?: string;
  marketingBudgetPeriod?: "weekly" | "monthly";
  marketingBudgetAmount?: string;
};

const CONFIG_SWITCH_CLASS = "h-4 w-7 border";
const CONFIG_SWITCH_THUMB_CLASS = "h-3 w-3 shadow-sm data-[state=checked]:translate-x-3";
const DEFAULT_CAMPAIGN_SCHEDULE_DAYS = ["mon", "tue", "wed", "thu", "fri"];
const CAMPAIGN_SCHEDULE_DAYS = [
  { key: "sun", label: "Sun" },
  { key: "mon", label: "Mon" },
  { key: "tue", label: "Tue" },
  { key: "wed", label: "Wed" },
  { key: "thu", label: "Thu" },
  { key: "fri", label: "Fri" },
  { key: "sat", label: "Sat" },
] as const;
const CAMPAIGN_SCHEDULE_TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "UTC",
] as const;

const SOURCE_TYPE_CONFIG: Record<SourceType, { label: string; icon: React.ElementType; color: string }> = {
  aggregator: { label: "Aggregator", icon: Globe, color: "text-info" },
  paid_ads: { label: "Paid Ads", icon: Target, color: "text-warning" },
  referral: { label: "Referral", icon: Users, color: "text-success" },
  partner: { label: "Partner", icon: Handshake, color: "text-accent" },
  organic: { label: "Organic", icon: Radio, color: "text-primary" },
  other: { label: "Other", icon: Zap, color: "text-muted-foreground" },
};

const VALIDATION_SERVICE_CONTROLS = [
  { key: "ipqs", label: "IPQS risk" },
  { key: "addressverify", label: "AddressVerify address" },
  { key: "smarty", label: "Smarty address" },
  { key: "kickbox", label: "Kickbox email" },
  { key: "ip2location", label: "IP location risk" },
  { key: "trustedform", label: "TrustedForm proof" },
  { key: "clearbit", label: "Clearbit enrichment" },
  { key: "pdl", label: "PDL enrichment" },
] as const;

const ABUSE_PROTECTION_NUMBER_CONTROLS = [
  {
    path: ["abuse_protection", "max_payload_bytes"],
    label: "Max payload bytes",
    fallback: 65536,
    min: 1024,
    step: 1024,
  },
  {
    path: ["abuse_protection", "max_requests_per_minute"],
    label: "Requests / minute",
    fallback: 30,
    min: 1,
    step: 1,
  },
  {
    path: ["abuse_protection", "max_requests_per_hour"],
    label: "Requests / hour",
    fallback: 300,
    min: 1,
    step: 1,
  },
  {
    path: ["abuse_protection", "max_requests_per_external_lead_10m"],
    label: "Same external lead / 10 min",
    fallback: 5,
    min: 1,
    step: 1,
  },
] as const;

const TOKEN_SCOPE_OPTIONS = [
  {
    value: "intake.submit",
    label: "Submit",
    description: "Can submit leads",
  },
  {
    value: "intake.read_internal",
    label: "Internal",
    description: "Can read internal response fields",
  },
  {
    value: "intake.read_validation",
    label: "Validation",
    description: "Can read validation details",
  },
  {
    value: "intake.read_debug",
    label: "Debug",
    description: "Can read debug output",
  },
] as const;

type TokenScopeValue = (typeof TOKEN_SCOPE_OPTIONS)[number]["value"];

type SortField = "revenue" | "jobs" | "closeRate" | "avgValue" | "cpa" | "roi";
type SortDir = "asc" | "desc";

function downloadCSV(filename: string, headers: string[], rows: (string | number)[][]) {
  const csv = [headers.join(","), ...rows.map((r) => r.map((c) => `"${c}"`).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function prettyJson(value: unknown) {
  return JSON.stringify(value ?? {}, null, 2);
}

function parseJsonObject(value: string): Record<string, unknown> {
  const parsed = JSON.parse(value || "{}");
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error("Config must be a JSON object.");
  }
  return parsed as Record<string, unknown>;
}

function toConfigForm(row: DbLeadSourceRow): LeadSourceConfigForm {
  const marketingBudget = getNestedConfigValue(row.config, ["marketing_budget"]);
  const marketingBudgetRecord = marketingBudget && typeof marketingBudget === "object" && !Array.isArray(marketingBudget)
    ? marketingBudget as Record<string, unknown>
    : {};
  const marketingBudgetPeriod = marketingBudgetRecord.period === "weekly" ? "weekly" : "monthly";
  const campaignSchedule = getNestedConfigValue(row.config, ["campaign_schedule"]);
  const campaignScheduleRecord = campaignSchedule && typeof campaignSchedule === "object" && !Array.isArray(campaignSchedule)
    ? campaignSchedule as Record<string, unknown>
    : {};
  const campaignScheduleDays = Array.isArray(campaignScheduleRecord.days)
    ? campaignScheduleRecord.days.map((day) => String(day).trim().toLowerCase()).filter(Boolean)
    : DEFAULT_CAMPAIGN_SCHEDULE_DAYS;

  return {
    duplicateWindowDays: String(row.duplicate_window_days ?? 30),
    requireTcpa: Boolean(row.require_tcpa),
    allowedZipCodes: Array.isArray(row.allowed_zip_codes) ? row.allowed_zip_codes.join("\n") : "",
    marketingBudgetPeriod,
    marketingBudgetAmount: configStringValue(marketingBudgetRecord.amount),
    campaignScheduleEnabled: campaignScheduleRecord.enabled === true,
    campaignScheduleTimezone: typeof campaignScheduleRecord.timezone === "string" && campaignScheduleRecord.timezone.trim()
      ? campaignScheduleRecord.timezone
      : "America/New_York",
    campaignScheduleDays,
    campaignScheduleStartTime: typeof campaignScheduleRecord.start_time === "string" && campaignScheduleRecord.start_time.trim()
      ? campaignScheduleRecord.start_time
      : "08:00",
    campaignScheduleEndTime: typeof campaignScheduleRecord.end_time === "string" && campaignScheduleRecord.end_time.trim()
      ? campaignScheduleRecord.end_time
      : "20:00",
  };
}

function parseZipList(value: string): string[] {
  return value
    .split(/[\n,]/)
    .map((part) => part.trim())
    .filter(Boolean);
}

function configStringValue(value: unknown) {
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  if (typeof value === "string") return value;
  return "";
}

function makeProjectTypeRuleId() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return `ptr_${crypto.randomUUID()}`;
  }
  return `ptr_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

function makeEmptyProjectTypeRule(): ProjectTypeRuleDraft {
  return {
    id: makeProjectTypeRuleId(),
    projectType: "",
    active: true,
    minBudget: "",
    maxBudget: "",
    minLeadCost: "",
    maxLeadCost: "",
    marketingBudgetPeriod: "monthly",
    marketingBudgetAmount: "",
    allowedZipCodes: "",
  };
}

function slugifySourceCode(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 48);
}

function normalizeSourceName(value: string) {
  return value.trim().replace(/\s+/g, " ").toLowerCase();
}

function getProjectTypeRules(config: Record<string, unknown> | null): ProjectTypeRuleDraft[] {
  const rawRules = getNestedConfigValue(config, ["project_type_rules"]);
  if (!Array.isArray(rawRules)) return [];

  return rawRules
    .filter((rule): rule is Record<string, unknown> => Boolean(rule && typeof rule === "object" && !Array.isArray(rule)))
    .map((rule, index) => {
      const allowedZipCodes = Array.isArray(rule.allowed_zip_codes)
        ? rule.allowed_zip_codes.map((zip) => String(zip).trim()).filter(Boolean).join("\n")
        : typeof rule.allowed_zip_codes === "string"
        ? rule.allowed_zip_codes
        : "";

      return {
        id: typeof rule.id === "string" && rule.id.trim() ? rule.id : `ptr_existing_${index}`,
        projectType: typeof rule.project_type === "string"
          ? rule.project_type
          : typeof rule.projectType === "string"
          ? rule.projectType
          : "",
        active: rule.active !== false,
        minBudget: configStringValue(rule.min_budget ?? rule.minBudget),
        maxBudget: configStringValue(rule.max_budget ?? rule.maxBudget),
        minLeadCost: configStringValue(rule.min_lead_cost ?? rule.minLeadCost),
        maxLeadCost: configStringValue(rule.max_lead_cost ?? rule.maxLeadCost),
        marketingBudgetPeriod: rule.marketing_budget_period === "weekly" ? "weekly" : "monthly",
        marketingBudgetAmount: configStringValue(rule.marketing_budget_amount ?? rule.marketingBudgetAmount),
        allowedZipCodes,
      };
    });
}

function numericConfigValue(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : trimmed;
}

function serializeProjectTypeRule(
  rule: ProjectTypeRuleDraft,
  options: { trimText?: boolean } = {},
) {
  const projectType = options.trimText ? rule.projectType.trim() : rule.projectType;
  return {
    id: rule.id,
    project_type: projectType,
    active: rule.active,
    min_budget: numericConfigValue(rule.minBudget),
    max_budget: numericConfigValue(rule.maxBudget),
    min_lead_cost: numericConfigValue(rule.minLeadCost),
    max_lead_cost: numericConfigValue(rule.maxLeadCost),
    marketing_budget_period: rule.marketingBudgetPeriod,
    marketing_budget_amount: numericConfigValue(rule.marketingBudgetAmount),
    allowed_zip_codes: parseZipList(rule.allowedZipCodes),
  };
}

function validateProjectTypeRules(rules: ProjectTypeRuleDraft[]) {
  for (const [index, rule] of rules.entries()) {
    const label = rule.projectType.trim() || `Rule ${index + 1}`;
    if (rule.active && !rule.projectType.trim()) {
      return `${label} needs a project type or should be inactive.`;
    }

    const numericFields = [
      { label: "minimum project value", value: rule.minBudget },
      { label: "maximum project value", value: rule.maxBudget },
      { label: "minimum lead cost", value: rule.minLeadCost },
      { label: "maximum lead cost", value: rule.maxLeadCost },
      { label: "spend budget", value: rule.marketingBudgetAmount },
    ];

    for (const field of numericFields) {
      if (field.value.trim() === "") continue;
      const parsed = Number(field.value);
      if (!Number.isFinite(parsed) || parsed < 0) {
        return `${label} ${field.label} must be zero or greater.`;
      }
    }

    const minBudget = rule.minBudget.trim() === "" ? null : Number(rule.minBudget);
    const maxBudget = rule.maxBudget.trim() === "" ? null : Number(rule.maxBudget);
    if (minBudget != null && maxBudget != null && minBudget > maxBudget) {
      return `${label} minimum project value cannot be greater than maximum project value.`;
    }

    const minLeadCost = rule.minLeadCost.trim() === "" ? null : Number(rule.minLeadCost);
    const maxLeadCost = rule.maxLeadCost.trim() === "" ? null : Number(rule.maxLeadCost);
    if (minLeadCost != null && maxLeadCost != null && minLeadCost > maxLeadCost) {
      return `${label} minimum lead cost cannot be greater than maximum lead cost.`;
    }
  }

  return null;
}

function deepCloneConfig(config: Record<string, unknown>) {
  return JSON.parse(JSON.stringify(config)) as Record<string, unknown>;
}

function setNestedConfigValue(
  config: Record<string, unknown>,
  path: string[],
  value: unknown,
) {
  const next = deepCloneConfig(config);
  let cursor: Record<string, unknown> = next;
  path.slice(0, -1).forEach((key) => {
    const existing = cursor[key];
    if (!existing || typeof existing !== "object" || Array.isArray(existing)) {
      cursor[key] = {};
    }
    cursor = cursor[key] as Record<string, unknown>;
  });
  cursor[path[path.length - 1]] = value;
  return next;
}

function getNestedConfigValue(config: Record<string, unknown> | null, path: string[]) {
  let cursor: unknown = config;
  for (const key of path) {
    if (!cursor || typeof cursor !== "object" || Array.isArray(cursor)) return undefined;
    cursor = (cursor as Record<string, unknown>)[key];
  }
  return cursor;
}

function configNumberValue(
  config: Record<string, unknown> | null,
  path: string[],
  fallback: number,
) {
  const value = getNestedConfigValue(config, path);
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  if (typeof value === "string" && value.trim() !== "" && Number.isFinite(Number(value))) {
    return value;
  }
  return String(fallback);
}

function maskSecret(secret: string | null | undefined) {
  if (!secret) return "Not set";
  if (secret.length <= 10) return "••••••";
  return `${secret.slice(0, 6)}••••••${secret.slice(-4)}`;
}

function generateWebhookSecret(prefix = "whsec") {
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  const token = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${prefix}_${token}`;
}

function getResponseAccessTokens(config: Record<string, unknown> | null): ResponseAccessToken[] {
  const tokens = getNestedConfigValue(config, ["response_access", "tokens"]);
  if (!Array.isArray(tokens)) return [];
  return tokens
    .filter((token): token is Record<string, unknown> => Boolean(token && typeof token === "object" && !Array.isArray(token)))
    .map((token) => ({
      ...token,
      label: typeof token.label === "string" ? token.label : undefined,
      name: typeof token.name === "string" ? token.name : undefined,
      id: typeof token.id === "string" ? token.id : undefined,
      secret: typeof token.secret === "string" ? token.secret : undefined,
      scopes: Array.isArray(token.scopes)
        ? token.scopes.filter((scope): scope is string => typeof scope === "string")
        : undefined,
    }));
}

function normalizeTokenScopes(scopes: string[] | undefined): TokenScopeValue[] {
  const allowed = new Set<TokenScopeValue>(TOKEN_SCOPE_OPTIONS.map((scope) => scope.value));
  const normalized = (scopes ?? []).filter((scope): scope is TokenScopeValue =>
    allowed.has(scope as TokenScopeValue),
  );
  return normalized.length ? normalized : ["intake.submit"];
}

function toggleTokenScope(scopes: string[] | undefined, scope: TokenScopeValue): TokenScopeValue[] {
  const current = normalizeTokenScopes(scopes);
  if (current.includes(scope)) {
    return current.length > 1 ? current.filter((item) => item !== scope) : current;
  }
  return [...current, scope];
}

function ScopeToggleGroup({
  scopes,
  onToggle,
  disabled = false,
}: {
  scopes: string[] | undefined;
  onToggle: (scope: TokenScopeValue) => void;
  disabled?: boolean;
}) {
  const normalized = normalizeTokenScopes(scopes);
  return (
    <div className="flex flex-wrap gap-1.5">
      {TOKEN_SCOPE_OPTIONS.map((scope) => {
        const selected = normalized.includes(scope.value);
        return (
          <Button
            key={scope.value}
            type="button"
            variant="outline"
            size="sm"
            title={scope.description}
            className={`h-6 px-2 text-[10px] ${selected ? "border-primary/40 bg-primary/10 text-primary" : "text-muted-foreground"}`}
            disabled={disabled}
            onClick={() => onToggle(scope.value)}
          >
            {scope.label}
          </Button>
        );
      })}
    </div>
  );
}

/* ═══ Component ═══ */
export default function LeadSourceManagement() {
  const { jobs, issues, credits } = useAppData();
  const [sources, setSources] = useState<LeadSource[]>([]);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortField, setSortField] = useState<SortField>("revenue");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detailMode, setDetailMode] = useState<"view" | "config">("view");
  const [showAdd, setShowAdd] = useState(false);
  const [newSource, setNewSource] = useState<NewLeadSourceDraft>({
    sourceType: "other",
    active: false,
    growthChannelId: undefined,
    marketingBudgetPeriod: "monthly",
    marketingBudgetAmount: "",
  });
  const [newSourceProjectRule, setNewSourceProjectRule] = useState<ProjectTypeRuleDraft>(() => makeEmptyProjectTypeRule());
  const [addingSource, setAddingSource] = useState(false);
  const [rawSource, setRawSource] = useState<DbLeadSourceRow | null>(null);
  const [rawSourceLoading, setRawSourceLoading] = useState(false);
  const [configText, setConfigText] = useState("{}");
  const [configForm, setConfigForm] = useState<LeadSourceConfigForm>({
    duplicateWindowDays: "30",
    requireTcpa: false,
    allowedZipCodes: "",
    marketingBudgetPeriod: "monthly",
    marketingBudgetAmount: "",
    campaignScheduleEnabled: false,
    campaignScheduleTimezone: "America/New_York",
    campaignScheduleDays: DEFAULT_CAMPAIGN_SCHEDULE_DAYS,
    campaignScheduleStartTime: "08:00",
    campaignScheduleEndTime: "20:00",
  });
  const [configDirty, setConfigDirty] = useState(false);
  const [configEditing, setConfigEditing] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const [newScopedTokenScopes, setNewScopedTokenScopes] = useState<TokenScopeValue[]>(["intake.submit"]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const { data } = await LeadSourceAnalyticsService.getAll();
      if (cancelled) return;
      const mapped = (data ?? []).map((row) => ({
        id: row.id,
        name: row.name,
        sourceType: (row.sourceType as SourceType) || "other",
        active: row.active,
        campaign: row.campaign || "",
        notes: row.notes || "",
        totalLeads: row.totalLeads || 0,
        contacted: row.contacted || 0,
        appointmentsSet: row.appointmentsSet || 0,
        appointmentsCompleted: row.appointmentsCompleted || 0,
        monthlyCost: 0,
        growthChannelId: DEFAULT_SOURCE_CHANNEL_MAP[row.name] || suggestChannel(row.name) || undefined,
      }));
      setSources(mapped);
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (!selectedId) {
      setRawSource(null);
      setConfigText("{}");
      setConfigDirty(false);
      setConfigEditing(false);
      setConfigError(null);
      setNewScopedTokenScopes(["intake.submit"]);
      return;
    }

    const loadRawSource = async () => {
      setRawSourceLoading(true);
      setConfigError(null);
      const { data, error } = await LeadSourceAnalyticsService.getById(selectedId);
      if (cancelled) return;

      if (error || !data) {
        setRawSource(null);
        setConfigText("{}");
        setConfigDirty(false);
        setConfigError(error?.message ?? "Could not load lead source config.");
        toast.error("Could not load lead source config.");
      } else {
        setRawSource(data);
        setConfigText(prettyJson(data.config));
        setConfigForm(toConfigForm(data));
        setConfigDirty(false);
        setConfigEditing(false);
        setNewScopedTokenScopes(["intake.submit"]);
      }

      setRawSourceLoading(false);
    };

    void loadRawSource();

    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  const toggleSort = (field: SortField) => {
    if (sortField === field) setSortDir((d) => d === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("desc"); }
  };

  /* ── Compute performance per source ── */
  const sourcePerformance = useMemo(() => {
    const map = new Map<string, {
      jobs: number; revenue: number; margin: number; closed: number; installed: number;
      issueCount: number; creditCount: number; creditAmount: number; jobIds: string[];
    }>();
    jobs.forEach((j) => {
      const src = j.marketing?.leadSource || "Unknown";
      const entry = map.get(src) || { jobs: 0, revenue: 0, margin: 0, closed: 0, installed: 0, issueCount: 0, creditCount: 0, creditAmount: 0, jobIds: [] };
      entry.jobs++;
      entry.revenue += getJobDisplayValue(j);
      entry.margin += j.financials?.marginEstimate ?? 0;
      if (j.currentStage === "Completed") { entry.closed++; entry.installed++; }
      if (j.marketing?.saleOutcome === "Closed") entry.closed = Math.max(entry.closed, entry.closed);
      entry.issueCount += issues.filter((i) => i.jobId === j.id).length;
      const jobCredits = credits.filter((c) => c.jobId === j.id);
      entry.creditCount += jobCredits.length;
      entry.creditAmount += jobCredits.reduce((s, c) => s + c.creditAmount, 0);
      entry.jobIds.push(j.id);
      map.set(src, entry);
    });
    return map;
  }, [jobs, issues, credits]);

  /* ── Enrich sources with full funnel + performance ── */
  const enrichedSources = useMemo(() => {
    let list = sources.map((s) => {
      const perf = sourcePerformance.get(s.name) || { jobs: 0, revenue: 0, margin: 0, closed: 0, installed: 0, issueCount: 0, creditCount: 0, creditAmount: 0, jobIds: [] };
      const closeRate = perf.jobs > 0 ? Math.round((perf.closed / perf.jobs) * 100) : 0;
      const avgValue = perf.jobs > 0 ? Math.round(perf.revenue / perf.jobs) : 0;
      // Funnel rates
      const connectionRate = s.totalLeads > 0 ? Math.round((s.contacted / s.totalLeads) * 100) : 0;
      const bookingRate = s.contacted > 0 ? Math.round((s.appointmentsSet / s.contacted) * 100) : 0;
      const showRate = s.appointmentsSet > 0 ? Math.round((s.appointmentsCompleted / s.appointmentsSet) * 100) : 0;
      const saleRate = s.appointmentsCompleted > 0 ? Math.round((perf.closed / s.appointmentsCompleted) * 100) : 0;
      // Cost metrics
      const totalCost = s.monthlyCost || 0;
      const cpl = totalCost > 0 && s.totalLeads > 0 ? Math.round(totalCost / s.totalLeads) : 0;
      const cpa = totalCost > 0 && perf.closed > 0 ? Math.round(totalCost / perf.closed) : 0;
      const roi = totalCost > 0 ? Math.round(((perf.revenue - totalCost) / totalCost) * 100) : 0;
      // Operational
      const issueRate = perf.jobs > 0 ? Math.round((perf.issueCount / perf.jobs) * 100) / 100 : 0;
      const avgCredit = perf.jobs > 0 ? Math.round(perf.creditAmount / perf.jobs) : 0;

      // Intelligence layer
      const netConversionPct = s.totalLeads > 0 ? Math.round((perf.closed / s.totalLeads) * 100) : 0;
      const leadQualityPct = s.totalLeads > 0 ? Math.round((s.appointmentsCompleted / s.totalLeads) * 100) : 0;
      const ranking = rankSource(netConversionPct, cpa, leadQualityPct);
      const budgetRec = getBudgetRecommendation(ranking.rank, cpa, leadQualityPct, roi);

      return {
        ...s, ...perf, closeRate, avgValue,
        connectionRate, bookingRate, showRate, saleRate,
        totalCost, cpl, cpa, roi, issueRate, avgCredit,
        netConversionPct, leadQualityPct, ranking, budgetRec,
      };
    });

    if (typeFilter !== "all") list = list.filter((s) => s.sourceType === typeFilter);
    if (statusFilter === "active") list = list.filter((s) => s.active);
    else if (statusFilter === "inactive") list = list.filter((s) => !s.active);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((s) => s.name.toLowerCase().includes(q) || s.campaign.toLowerCase().includes(q));
    }

    list.sort((a, b) => {
      const av = a[sortField] ?? 0;
      const bv = b[sortField] ?? 0;
      const cmp = (av as number) - (bv as number);
      return sortDir === "asc" ? cmp : -cmp;
    });

    return list;
  }, [sources, sourcePerformance, typeFilter, statusFilter, search, sortField, sortDir]);

  /* ── Totals ── */
  const totals = useMemo(() => {
    const active = sources.filter((s) => s.active).length;
    const totalRevenue = enrichedSources.reduce((s, e) => s + e.revenue, 0);
    const totalJobs = enrichedSources.reduce((s, e) => s + e.jobs, 0);
    const totalClosed = enrichedSources.reduce((s, e) => s + e.closed, 0);
    const totalCost = enrichedSources.reduce((s, e) => s + e.totalCost, 0);
    const totalLeads = enrichedSources.reduce((s, e) => s + e.totalLeads, 0);
    const overallClose = totalJobs > 0 ? Math.round((totalClosed / totalJobs) * 100) : 0;
    const overallCPA = totalCost > 0 && totalClosed > 0 ? Math.round(totalCost / totalClosed) : 0;
    const overallROI = totalCost > 0 ? Math.round(((totalRevenue - totalCost) / totalCost) * 100) : 0;
    const totalCredits = enrichedSources.reduce((s, e) => s + e.creditAmount, 0);
    return { active, total: sources.length, totalRevenue, totalJobs, totalLeads, overallClose, totalCost, overallCPA, overallROI, totalCredits };
  }, [sources, enrichedSources]);

  const selectedSource = selectedId ? enrichedSources.find((s) => s.id === selectedId) : null;
  const parsedConfig = useMemo(() => {
    try {
      return parseJsonObject(configText);
    } catch {
      return null;
    }
  }, [configText]);
  const scopedAccessTokens = useMemo(() => getResponseAccessTokens(parsedConfig), [parsedConfig]);
  const projectTypeRules = useMemo(() => getProjectTypeRules(parsedConfig), [parsedConfig]);
  const payloadContactRequirements = useMemo(
    () => getPayloadContactRequirements(parsedConfig),
    [parsedConfig],
  );
  const requiredPayloadFields = useMemo(
    () => getRequiredPayloadFields(parsedConfig),
    [parsedConfig],
  );
  const blacklistAllianceConfig = useMemo(
    () => getBlacklistAllianceConfig(parsedConfig),
    [parsedConfig],
  );

  const resetNewSourceDraft = () => {
    setNewSource({
      sourceType: "other",
      active: false,
      growthChannelId: undefined,
      marketingBudgetPeriod: "monthly",
      marketingBudgetAmount: "",
    });
    setNewSourceProjectRule(makeEmptyProjectTypeRule());
  };

  const handleAdd = async () => {
    if (!newSource.name?.trim()) { toast.error("Source name is required."); return; }
    const normalizedNewName = normalizeSourceName(newSource.name);
    if (sources.some((source) => normalizeSourceName(source.name) === normalizedNewName)) {
      toast.error("This lead source already exists.", {
        description: "Open the existing source and add project type rules from Config instead.",
      });
      return;
    }
    const sourceCode = (newSource.code?.trim() || slugifySourceCode(newSource.name));
    if (!sourceCode) { toast.error("Source code is required."); return; }
    const newSourceMarketingBudgetAmount = newSource.marketingBudgetAmount?.trim()
      ? Number(newSource.marketingBudgetAmount)
      : null;
    if (
      newSourceMarketingBudgetAmount != null &&
      (!Number.isFinite(newSourceMarketingBudgetAmount) || newSourceMarketingBudgetAmount < 0)
    ) {
      toast.error("Source spend budget must be zero or greater.");
      return;
    }
    const ruleValidationError = validateProjectTypeRules([newSourceProjectRule]);
    if (ruleValidationError) {
      toast.error(ruleValidationError);
      return;
    }

    const suggested = suggestChannel(newSource.name);
    const growthChannelId = newSource.growthChannelId || suggested || undefined;
    const config: Record<string, unknown> = {
      marketing_budget: {
        period: newSource.marketingBudgetPeriod ?? "monthly",
        amount: newSourceMarketingBudgetAmount,
      },
      campaign_schedule: {
        enabled: false,
        timezone: "America/New_York",
        days: DEFAULT_CAMPAIGN_SCHEDULE_DAYS,
        start_time: "08:00",
        end_time: "20:00",
        reject_outside_schedule: true,
      },
      project_type_rules: [
        serializeProjectTypeRule(newSourceProjectRule, { trimText: true }),
      ],
      services: {
        blacklist_alliance: getDefaultBlacklistAllianceServiceConfig(),
      },
    };

    setAddingSource(true);
    try {
      const organizationId = await getCurrentOrganizationId();
      const { data, error } = await LeadSourceAnalyticsService.create({
        organization_id: organizationId,
        name: newSource.name.trim(),
        code: sourceCode,
        source_type: (newSource.sourceType as SourceType) || "other",
        campaign: newSource.campaign?.trim() || null,
        status: "inactive",
        webhook_secret: generateWebhookSecret(),
        duplicate_window_days: 30,
        require_tcpa: false,
        field_mapping: {},
        config,
        allowed_zip_codes: [],
        is_test_mode: false,
        is_active: false,
      });

      if (error || !data) {
        toast.error("Could not add lead source.", {
          description: error?.message,
        });
        return;
      }

      await refreshSources();
      resetNewSourceDraft();
      setShowAdd(false);

      const ch = growthChannelId ? getChannelById(growthChannelId) : null;
      toast.success(`Source "${data.name}" added inactive.${ch ? ` Mapped to ${ch.name}.` : ""}`);
      setDetailMode("config");
      setSelectedId(data.id);
    } catch (error) {
      toast.error("Could not add lead source.", {
        description: error instanceof Error ? error.message : undefined,
      });
    } finally {
      setAddingSource(false);
    }
  };

  const toggleActive = (id: string) => {
    setSources((prev) => prev.map((s) => s.id === id ? { ...s, active: !s.active } : s));
  };

  const refreshSources = async () => {
    const { data } = await LeadSourceAnalyticsService.getAll();
    const mapped = (data ?? []).map((row) => ({
      id: row.id,
      name: row.name,
      sourceType: (row.sourceType as SourceType) || "other",
      active: row.active,
      campaign: row.campaign || "",
      notes: row.notes || "",
      totalLeads: row.totalLeads || 0,
      contacted: row.contacted || 0,
      appointmentsSet: row.appointmentsSet || 0,
      appointmentsCompleted: row.appointmentsCompleted || 0,
      monthlyCost: 0,
      growthChannelId: DEFAULT_SOURCE_CHANNEL_MAP[row.name] || suggestChannel(row.name) || undefined,
    }));
    setSources(mapped);
  };

  const setConfigFormValue = <K extends keyof LeadSourceConfigForm>(
    key: K,
    value: LeadSourceConfigForm[K],
  ) => {
    if (!configEditing) return;
    setConfigForm((current) => ({ ...current, [key]: value }));
    setConfigDirty(true);
  };

  const toggleCampaignScheduleDay = (day: string) => {
    if (!configEditing) return;
    setConfigForm((current) => {
      const currentDays = current.campaignScheduleDays;
      const nextDays = currentDays.includes(day)
        ? currentDays.filter((item) => item !== day)
        : [...currentDays, day];
      return { ...current, campaignScheduleDays: nextDays };
    });
    setConfigDirty(true);
  };

  const updateConfigDraft = (path: string[], value: unknown) => {
    if (!configEditing) return;
    try {
      const current = parseJsonObject(configText);
      const next = setNestedConfigValue(current, path, value);
      setConfigText(prettyJson(next));
      setConfigError(null);
      setConfigDirty(true);
    } catch (error) {
      setConfigError(error instanceof Error ? error.message : "Config JSON is invalid.");
    }
  };

  const updateConfigDraftMany = (updates: Array<{ path: string[]; value: unknown }>) => {
    if (!configEditing) return;
    try {
      let next = parseJsonObject(configText);
      updates.forEach((update) => {
        next = setNestedConfigValue(next, update.path, update.value);
      });
      setConfigText(prettyJson(next));
      setConfigError(null);
      setConfigDirty(true);
    } catch (error) {
      setConfigError(error instanceof Error ? error.message : "Config JSON is invalid.");
    }
  };

  const updateBlacklistAllianceDraft = (
    checkKey: BlacklistAllianceCheckKey,
    patch: { enabled?: boolean; intake?: boolean },
  ) => {
    if (!configEditing) return;
    try {
      const current = parseJsonObject(configText);
      const next = checkKey === "email_filter" &&
          typeof patch.enabled === "boolean"
        ? updateBlacklistAllianceEmailFilterFromUi(current, patch.enabled)
        : updateBlacklistAllianceCheck(current, checkKey, patch);
      setConfigText(prettyJson(next));
      setConfigError(null);
      setConfigDirty(true);
    } catch (error) {
      setConfigError(error instanceof Error ? error.message : "Config JSON is invalid.");
    }
  };

  const setProjectTypeRulesDraft = (rules: ProjectTypeRuleDraft[]) => {
    if (!configEditing) return;
    updateConfigDraft(["project_type_rules"], rules.map(serializeProjectTypeRule));
  };

  const handleAddProjectTypeRule = () => {
    if (!configEditing) return;
    const nextRule: ProjectTypeRuleDraft = {
      id: makeProjectTypeRuleId(),
      projectType: "",
      active: true,
      minBudget: "",
      maxBudget: "",
      minLeadCost: "",
      maxLeadCost: "",
      marketingBudgetPeriod: "monthly",
      marketingBudgetAmount: "",
      allowedZipCodes: "",
    };
    setProjectTypeRulesDraft([...projectTypeRules, nextRule]);
  };

  const handleUpdateProjectTypeRule = (
    index: number,
    patch: Partial<ProjectTypeRuleDraft>,
  ) => {
    if (!configEditing) return;
    const nextRules = projectTypeRules.map((rule, ruleIndex) =>
      ruleIndex === index ? { ...rule, ...patch } : rule,
    );
    setProjectTypeRulesDraft(nextRules);
  };

  const handleRemoveProjectTypeRule = (index: number) => {
    if (!configEditing) return;
    setProjectTypeRulesDraft(projectTypeRules.filter((_, ruleIndex) => ruleIndex !== index));
  };

  const handleStartConfigEdit = () => {
    setConfigEditing(true);
    setConfigError(null);
  };

  const handleCancelConfigEdit = () => {
    if (!rawSource) return;
    setConfigText(prettyJson(rawSource.config));
    setConfigForm(toConfigForm(rawSource));
    setConfigDirty(false);
    setConfigError(null);
    setConfigEditing(false);
    setNewScopedTokenScopes(["intake.submit"]);
  };

  const handleRequestCloseDetail = () => {
    if (configDirty) {
      toast.warning("Save or cancel config changes before closing.");
      return;
    }
    setConfigEditing(false);
    setDetailMode("view");
    setSelectedId(null);
  };

  const handleOpenSourceDetail = (sourceId: string, mode: "view" | "config") => {
    if (selectedId && configDirty) {
      toast.warning("Save or cancel config changes before switching sources.");
      return;
    }

    setDetailMode(mode);
    setSelectedId(sourceId);
  };

  const copySecretToClipboard = async (secret: string | null | undefined, label: string) => {
    if (!secret) {
      toast.error(`${label} is not set.`);
      return;
    }

    try {
      await navigator.clipboard.writeText(secret);
      toast.success(`${label} copied.`);
    } catch {
      toast.error("Could not copy token. Browser clipboard access was blocked.");
    }
  };

  const handleRotatePrimaryWebhookSecret = async () => {
    if (!rawSource) return;
    if (!configEditing) return;
    const confirmed = window.confirm(
      "Rotate the primary webhook secret? Existing integrations using this secret will stop working until updated.",
    );
    if (!confirmed) return;

    const nextSecret = generateWebhookSecret("whsec");
    setConfigSaving(true);
    const { data, error } = await LeadSourceAnalyticsService.update(rawSource.id, {
      webhook_secret: nextSecret,
    });
    setConfigSaving(false);

    if (error || !data) {
      toast.error("Could not rotate webhook secret.", {
        description: error?.message,
      });
      return;
    }

    setRawSource(data);
    toast.success("Primary webhook secret rotated.", {
      description: "Copy the new secret and update the provider integration.",
    });
  };

  const handleAddScopedAccessToken = () => {
    if (!configEditing) return;
    try {
      const current = parseJsonObject(configText);
      const tokens = getResponseAccessTokens(current);
      const selectedScopes = normalizeTokenScopes(newScopedTokenScopes);
      const newToken: ResponseAccessToken = {
        label: `portal-token-${tokens.length + 1}`,
        secret: generateWebhookSecret("lsat"),
        scopes: selectedScopes,
      };
      const next = setNestedConfigValue(current, ["response_access", "tokens"], [...tokens, newToken]);
      setConfigText(prettyJson(next));
      setConfigError(null);
      setConfigDirty(true);
      toast.success("Scoped access token added.", {
        description: "Save config before using this token.",
      });
    } catch (error) {
      setConfigError(error instanceof Error ? error.message : "Config JSON is invalid.");
    }
  };

  const handleToggleNewScopedTokenScope = (scope: TokenScopeValue) => {
    if (!configEditing) return;
    setNewScopedTokenScopes((current) => toggleTokenScope(current, scope));
  };

  const handleToggleScopedAccessTokenScope = (index: number, scope: TokenScopeValue) => {
    if (!configEditing) return;
    try {
      const current = parseJsonObject(configText);
      const tokens = getResponseAccessTokens(current);
      const token = tokens[index];
      if (!token) return;
      const nextTokens = tokens.map((item, tokenIndex) =>
        tokenIndex === index
          ? { ...item, scopes: toggleTokenScope(item.scopes, scope) }
          : item,
      );
      const next = setNestedConfigValue(current, ["response_access", "tokens"], nextTokens);
      setConfigText(prettyJson(next));
      setConfigError(null);
      setConfigDirty(true);
    } catch (error) {
      setConfigError(error instanceof Error ? error.message : "Config JSON is invalid.");
    }
  };

  const handleRenameScopedAccessToken = (index: number, label: string) => {
    if (!configEditing) return;
    try {
      const current = parseJsonObject(configText);
      const tokens = getResponseAccessTokens(current);
      const token = tokens[index];
      if (!token) return;
      const trimmed = label.trimStart();
      const nextTokens = tokens.map((item, tokenIndex) =>
        tokenIndex === index
          ? { ...item, label: trimmed }
          : item,
      );
      const next = setNestedConfigValue(current, ["response_access", "tokens"], nextTokens);
      setConfigText(prettyJson(next));
      setConfigError(null);
      setConfigDirty(true);
    } catch (error) {
      setConfigError(error instanceof Error ? error.message : "Config JSON is invalid.");
    }
  };

  const handleRemoveScopedAccessToken = (index: number) => {
    if (!configEditing) return;
    try {
      const current = parseJsonObject(configText);
      const tokens = getResponseAccessTokens(current);
      const nextTokens = tokens.filter((_, tokenIndex) => tokenIndex !== index);
      const next = setNestedConfigValue(current, ["response_access", "tokens"], nextTokens);
      setConfigText(prettyJson(next));
      setConfigError(null);
      setConfigDirty(true);
    } catch (error) {
      setConfigError(error instanceof Error ? error.message : "Config JSON is invalid.");
    }
  };

  const handleConfigTextChange = (value: string) => {
    if (!configEditing) return;
    setConfigText(value);
    setConfigDirty(true);
    try {
      parseJsonObject(value);
      setConfigError(null);
    } catch (error) {
      setConfigError(error instanceof Error ? error.message : "Config JSON is invalid.");
    }
  };

  const handleSaveSourceConfig = async () => {
    if (!rawSource) return;

    let config: Record<string, unknown>;
    try {
      config = parseJsonObject(configText);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Config JSON is invalid.";
      setConfigError(message);
      toast.error(message);
      return;
    }

    const duplicateWindowDays = Number(configForm.duplicateWindowDays);
    if (!Number.isFinite(duplicateWindowDays) || duplicateWindowDays < 0) {
      toast.error("Duplicate window must be zero or greater.");
      return;
    }

    const sourceMarketingBudgetAmount = configForm.marketingBudgetAmount.trim() === ""
      ? null
      : Number(configForm.marketingBudgetAmount);
    if (
      sourceMarketingBudgetAmount != null &&
      (!Number.isFinite(sourceMarketingBudgetAmount) || sourceMarketingBudgetAmount < 0)
    ) {
      toast.error("Source spend budget must be zero or greater.");
      return;
    }

    const rulesValidationError = validateProjectTypeRules(projectTypeRules);
    if (rulesValidationError) {
      toast.error(rulesValidationError);
      return;
    }
    config = setNestedConfigValue(config, ["marketing_budget"], {
      period: configForm.marketingBudgetPeriod,
      amount: sourceMarketingBudgetAmount,
    });
    config = setNestedConfigValue(config, ["campaign_schedule"], {
      enabled: configForm.campaignScheduleEnabled,
      timezone: configForm.campaignScheduleTimezone || "America/New_York",
      days: configForm.campaignScheduleDays,
      start_time: configForm.campaignScheduleStartTime || "08:00",
      end_time: configForm.campaignScheduleEndTime || "20:00",
      reject_outside_schedule: true,
    });
    config = setNestedConfigValue(
      config,
      ["project_type_rules"],
      projectTypeRules.map((rule) => serializeProjectTypeRule(rule, { trimText: true })),
    );

    setConfigSaving(true);
    const { data, error } = await LeadSourceAnalyticsService.update(rawSource.id, {
      duplicate_window_days: duplicateWindowDays,
      require_tcpa: configForm.requireTcpa,
      allowed_zip_codes: parseZipList(configForm.allowedZipCodes),
      config,
    });

    setConfigSaving(false);

    if (error || !data) {
      toast.error("Could not save lead source config.", {
        description: error?.message,
      });
      return;
    }

    setRawSource(data);
    setConfigText(prettyJson(data.config));
    setConfigForm(toConfigForm(data));
    setConfigDirty(false);
    setConfigEditing(false);
    setConfigError(null);
    await refreshSources();
    toast.success("Lead source config saved.");
  };

  const handleExport = () => {
    const rows = enrichedSources.map((s) => [
      s.name, SOURCE_TYPE_CONFIG[s.sourceType].label, s.active ? "Active" : "Inactive",
      s.totalLeads, s.contacted, s.appointmentsSet, s.appointmentsCompleted,
      s.jobs, s.closed, s.revenue, s.avgValue, s.closeRate,
      s.connectionRate, s.bookingRate, s.showRate, s.saleRate,
      s.totalCost, s.cpl, s.cpa, s.roi,
      s.issueCount, s.issueRate, s.creditAmount, s.avgCredit,
    ]);
    downloadCSV("lead-sources.csv", [
      "Source", "Type", "Status", "Leads", "Contacted", "Appts Set", "Appts Completed",
      "Jobs", "Closed", "Revenue", "Avg Value", "Close %",
      "Connection %", "Booking %", "Show %", "Sale %",
      "Cost", "CPL", "CPA", "ROI %",
      "Issues", "Issue Rate", "Credits $", "Avg Credit/Job",
    ], rows);
    toast.success("Exported lead source data.");
  };

  function SortIcon({ field }: { field: SortField }) {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-40" />;
    return sortDir === "asc" ? <ArrowUp className="h-3 w-3 ml-1 text-primary" /> : <ArrowDown className="h-3 w-3 ml-1 text-primary" />;
  }

  return (
    <div className="space-y-5 max-w-7xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-lg font-bold tracking-tight flex items-center gap-2">
            <Megaphone className="h-5 w-5 text-primary" />
            Lead Source Management
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            {totals.active} active sources · {totals.totalLeads} leads · {totals.totalJobs} jobs · ${totals.totalRevenue.toLocaleString()} revenue
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="/disposition-report">
            <Button variant="outline" size="sm" className="text-xs h-8 gap-1.5">
              <FileText className="h-3.5 w-3.5" /> Disposition Report
            </Button>
          </Link>
          <Button variant="outline" size="sm" className="text-xs h-8 gap-1.5" onClick={handleExport}>
            <Download className="h-3.5 w-3.5" /> Export
          </Button>
          <Link to="/marketing-attribution">
            <Button variant="outline" size="sm" className="text-xs h-8 gap-1.5">
              <BarChart3 className="h-3.5 w-3.5" /> Marketing Dashboard
            </Button>
          </Link>
          <Button size="sm" className="text-xs h-8 gap-1.5" onClick={() => setShowAdd(true)}>
            <Plus className="h-3.5 w-3.5" /> Add Source
          </Button>
        </div>
      </div>

      {/* ═══ KPIs ═══ */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
        {[
          { label: "Total Leads", value: totals.totalLeads, icon: Users, color: "text-info" },
          { label: "Active Sources", value: totals.active, icon: CheckCircle, color: "text-success" },
          { label: "Revenue", value: `$${totals.totalRevenue.toLocaleString()}`, icon: DollarSign, color: "text-success" },
          { label: "Jobs", value: totals.totalJobs, icon: Briefcase, color: "text-primary" },
          { label: "Close Rate", value: `${totals.overallClose}%`, icon: Percent, color: totals.overallClose >= 50 ? "text-success" : "text-warning" },
          { label: "Avg CPA", value: totals.overallCPA > 0 ? `$${totals.overallCPA}` : "—", icon: Target, color: "text-warning" },
          { label: "Overall ROI", value: totals.totalCost > 0 ? `${totals.overallROI}%` : "—", icon: TrendingUp, color: totals.overallROI > 200 ? "text-success" : "text-warning" },
          { label: "Credits Issued", value: `$${totals.totalCredits.toLocaleString()}`, icon: Receipt, color: totals.totalCredits > 0 ? "text-warning" : "text-muted-foreground" },
        ].map((kpi) => (
          <Card key={kpi.label} className="shadow-none">
            <CardContent className="p-2.5 text-center">
              <kpi.icon className={`h-3.5 w-3.5 mx-auto mb-1 ${kpi.color}`} />
              <p className="text-base font-bold leading-none">{kpi.value}</p>
              <p className="text-[8px] text-muted-foreground mt-1 font-medium uppercase tracking-wider">{kpi.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* ═══ Performance Charts ═══ */}
      {(() => {
        const CHART_COLORS = [
          "hsl(var(--primary))", "hsl(var(--chart-2))", "hsl(var(--chart-3))",
          "hsl(var(--chart-4))", "hsl(var(--chart-5))", "hsl(var(--chart-1))",
          "hsl(var(--accent))", "hsl(var(--muted-foreground))", "hsl(var(--info))", "hsl(var(--warning))",
        ];
        const activeSources = enrichedSources.filter((s) => s.active && s.jobs > 0);
        const chartData = activeSources.map((s) => ({
          name: s.name.length > 14 ? s.name.slice(0, 12) + "…" : s.name,
          revenue: s.revenue,
          jobs: s.jobs,
          cpa: s.cpa,
          roi: s.roi,
          avgValue: s.avgValue,
        }));
        const funnelData = activeSources.map((s) => ({
          name: s.name.length > 14 ? s.name.slice(0, 12) + "…" : s.name,
          leads: s.totalLeads,
          contacted: s.contacted,
          apptsSet: s.appointmentsSet,
          apptsCompleted: s.appointmentsCompleted,
          sold: s.closed,
          installed: s.installed,
        }));

        const ChartCard = ({ title, dataKey, prefix, suffix, data }: { title: string; dataKey: string; prefix?: string; suffix?: string; data: Record<string, any>[] }) => (
          <Card className="shadow-none">
            <CardContent className="p-3">
              <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-semibold mb-2">{title}</p>
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={data} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                  <XAxis dataKey="name" tick={{ fontSize: 9 }} angle={-30} textAnchor="end" height={50} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tick={{ fontSize: 9 }} width={45} stroke="hsl(var(--muted-foreground))" tickFormatter={(v: number) => `${prefix || ""}${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}${suffix || ""}`} />
                  <Tooltip
                    contentStyle={{ fontSize: 11, background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, color: "hsl(var(--popover-foreground))" }}
                    formatter={(value: number) => [`${prefix || ""}${value.toLocaleString()}${suffix || ""}`, title]}
                  />
                  <Bar dataKey={dataKey} radius={[3, 3, 0, 0]}>
                    {data.map((_, idx) => <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        );

        return chartData.length > 0 ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <ChartCard title="Revenue by Source" dataKey="revenue" prefix="$" data={chartData} />
              <ChartCard title="Jobs by Source" dataKey="jobs" data={chartData} />
              <ChartCard title="Avg Job Value by Source" dataKey="avgValue" prefix="$" data={chartData} />
              <ChartCard title="Cost Per Acquisition" dataKey="cpa" prefix="$" data={chartData.filter((d) => d.cpa > 0)} />
              <ChartCard title="ROI by Source" dataKey="roi" suffix="%" data={chartData.filter((d) => d.roi !== 0)} />
              {/* Funnel Comparison */}
              <Card className="shadow-none sm:col-span-2 lg:col-span-1">
                <CardContent className="p-3">
                  <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-semibold mb-2">Funnel Comparison</p>
                  <ResponsiveContainer width="100%" height={160}>
                    <BarChart data={funnelData} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                      <XAxis dataKey="name" tick={{ fontSize: 9 }} angle={-30} textAnchor="end" height={50} stroke="hsl(var(--muted-foreground))" />
                      <YAxis tick={{ fontSize: 9 }} width={35} stroke="hsl(var(--muted-foreground))" />
                      <Tooltip contentStyle={{ fontSize: 11, background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, color: "hsl(var(--popover-foreground))" }} />
                      <Bar dataKey="leads" fill="hsl(265 75% 65%)" radius={[2, 2, 0, 0]} name="Leads" />
                      <Bar dataKey="contacted" fill="hsl(var(--chart-1))" radius={[2, 2, 0, 0]} name="Contacted" />
                      <Bar dataKey="apptsSet" fill="hsl(var(--chart-2))" radius={[2, 2, 0, 0]} name="Appts Set" />
                      <Bar dataKey="sold" fill="hsl(var(--chart-3))" radius={[2, 2, 0, 0]} name="Sold" />
                      <Bar dataKey="installed" fill="hsl(var(--primary))" radius={[2, 2, 0, 0]} name="Installed" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          </>
        ) : null;
      })()}

      {/* ═══ Filters ═══ */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input placeholder="Search sources or campaigns..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-8 text-xs" />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="h-8 text-xs w-[140px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {Object.entries(SOURCE_TYPE_CONFIG).map(([key, cfg]) => (
              <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-8 text-xs w-[120px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* ═══ Source Table ═══ */}
      <Card className="shadow-none">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-[10px] font-semibold">Source</TableHead>
                  <TableHead className="text-[10px] font-semibold hidden sm:table-cell">Type</TableHead>
                  <TableHead className="text-[10px] font-semibold text-center">Status</TableHead>
                  <TableHead className="text-[10px] font-semibold text-right hidden md:table-cell">Leads</TableHead>
                  <TableHead
                    className="text-[10px] font-semibold text-right cursor-pointer select-none hover:text-primary transition-colors"
                    onClick={() => toggleSort("jobs")}
                  >
                    <span className="inline-flex items-center justify-end">Jobs <SortIcon field="jobs" /></span>
                  </TableHead>
                  <TableHead
                    className="text-[10px] font-semibold text-right cursor-pointer select-none hover:text-primary transition-colors hidden sm:table-cell"
                    onClick={() => toggleSort("revenue")}
                  >
                    <span className="inline-flex items-center justify-end">Revenue <SortIcon field="revenue" /></span>
                  </TableHead>
                  <TableHead
                    className="text-[10px] font-semibold text-right cursor-pointer select-none hover:text-primary transition-colors hidden md:table-cell"
                    onClick={() => toggleSort("closeRate")}
                  >
                    <span className="inline-flex items-center justify-end">Close% <SortIcon field="closeRate" /></span>
                  </TableHead>
                  <TableHead
                    className="text-[10px] font-semibold text-right cursor-pointer select-none hover:text-primary transition-colors hidden lg:table-cell"
                    onClick={() => toggleSort("cpa")}
                  >
                    <span className="inline-flex items-center justify-end">CPA <SortIcon field="cpa" /></span>
                  </TableHead>
                  <TableHead
                    className="text-[10px] font-semibold text-right cursor-pointer select-none hover:text-primary transition-colors hidden lg:table-cell"
                    onClick={() => toggleSort("roi")}
                  >
                    <span className="inline-flex items-center justify-end">ROI <SortIcon field="roi" /></span>
                  </TableHead>
                  <TableHead className="w-36 text-right text-[10px] font-semibold">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {enrichedSources.map((s) => {
                  const cfg = SOURCE_TYPE_CONFIG[s.sourceType];
                  const TypeIcon = cfg.icon;
                  return (
                    <TableRow key={s.id} className="group hover:bg-muted/40">
                      <TableCell className="py-2">
                        <div className="flex items-center gap-2">
                          <TypeIcon className={`h-3.5 w-3.5 shrink-0 ${cfg.color}`} />
                          <div>
                            <p className="text-xs font-semibold">{s.name}</p>
                            {s.campaign && <p className="text-[10px] text-muted-foreground truncate max-w-[180px]">{s.campaign}</p>}
                          {s.growthChannelId && (() => {
                              const ch = getChannelById(s.growthChannelId);
                              return ch ? (
                                <p className="text-[9px] text-primary/80 flex items-center gap-1 mt-0.5">
                                  <Link2 className="h-2.5 w-2.5" />
                                  Mapped to: {ch.name}
                                </p>
                              ) : null;
                            })()}
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <Badge variant="outline" className={`text-[9px] px-1.5 py-0 ${s.ranking.badgeClass}`}>
                              {s.ranking.label}
                            </Badge>
                          </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="py-2 hidden sm:table-cell">
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">{cfg.label}</Badge>
                      </TableCell>
                      <TableCell className="py-2 text-center">
                        <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${s.active ? "bg-success/10 text-success border-success/20" : "bg-muted text-muted-foreground border-border"}`}>
                          {s.active ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell className="py-2 text-right hidden md:table-cell">
                        <span className="text-xs text-muted-foreground">{s.totalLeads}</span>
                      </TableCell>
                      <TableCell className="py-2 text-right">
                        <span className="text-xs font-semibold">{s.jobs}</span>
                      </TableCell>
                      <TableCell className="py-2 text-right hidden sm:table-cell">
                        <span className="text-xs font-medium">${s.revenue.toLocaleString()}</span>
                      </TableCell>
                      <TableCell className="py-2 text-right hidden md:table-cell">
                        <span className={`text-xs font-medium ${s.closeRate >= 50 ? "text-success" : s.closeRate >= 25 ? "text-warning" : "text-muted-foreground"}`}>
                          {s.jobs > 0 ? `${s.closeRate}%` : "—"}
                        </span>
                      </TableCell>
                      <TableCell className="py-2 text-right hidden lg:table-cell">
                        <span className="text-xs text-muted-foreground">{s.cpa > 0 ? `$${s.cpa}` : "—"}</span>
                      </TableCell>
                      <TableCell className="py-2 text-right hidden lg:table-cell">
                        <span className={`text-xs font-medium ${s.roi > 200 ? "text-success" : s.roi > 0 ? "text-warning" : "text-muted-foreground"}`}>
                          {s.totalCost > 0 ? `${s.roi}%` : "—"}
                        </span>
                      </TableCell>
                      <TableCell className="py-2">
                        <div className="flex items-center justify-end gap-1.5">
                          <Button variant="ghost" size="sm" className="h-7 text-[10px] gap-1" onClick={() => handleOpenSourceDetail(s.id, "view")}>
                            <Eye className="h-3 w-3" /> View
                          </Button>
                          <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1" onClick={() => handleOpenSourceDetail(s.id, "config")}>
                            <Settings2 className="h-3 w-3" /> Config
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {enrichedSources.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-8 text-xs text-muted-foreground">No sources match your filters.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* ═══ Source Performance by Type ═══ */}
      <Card className="shadow-none">
        <CardHeader className="pb-2 px-4 pt-4">
          <CardTitle className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em] flex items-center gap-2">
            <BarChart3 className="h-3.5 w-3.5" /> Performance by Source Type
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          <div className="space-y-3">
            {Object.entries(SOURCE_TYPE_CONFIG).map(([key, cfg]) => {
              const typeSources = enrichedSources.filter((s) => s.sourceType === key);
              const typeRevenue = typeSources.reduce((sum, s) => sum + s.revenue, 0);
              const typeJobs = typeSources.reduce((sum, s) => sum + s.jobs, 0);
              const typeCost = typeSources.reduce((sum, s) => sum + s.totalCost, 0);
              const maxRevenue = Math.max(...Object.keys(SOURCE_TYPE_CONFIG).map((k) =>
                enrichedSources.filter((s) => s.sourceType === k).reduce((sum, s) => sum + s.revenue, 0)
              ), 1);
              if (typeSources.length === 0) return null;
              const TypeIcon = cfg.icon;
              return (
                <div key={key} className="flex items-center gap-3">
                  <div className="flex items-center gap-2 w-24 shrink-0">
                    <TypeIcon className={`h-3.5 w-3.5 ${cfg.color}`} />
                    <span className="text-[11px] font-medium">{cfg.label}</span>
                  </div>
                  <div className="flex-1">
                    <Progress value={(typeRevenue / maxRevenue) * 100} className="h-2" />
                  </div>
                  <div className="text-right w-40 shrink-0">
                    <span className="text-xs font-bold">${typeRevenue.toLocaleString()}</span>
                    <span className="text-[10px] text-muted-foreground ml-1.5">{typeJobs} jobs</span>
                    {typeCost > 0 && <span className="text-[10px] text-muted-foreground ml-1.5">· ${typeCost.toLocaleString()} cost</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* ═══ Add Source Dialog ═══ */}
      <Dialog
        open={showAdd}
        onOpenChange={(open) => {
          if (!open && !addingSource) resetNewSourceDraft();
          setShowAdd(open);
        }}
      >
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-sm flex items-center gap-2"><Plus className="h-4 w-4 text-primary" /> Add Lead Source</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md border bg-muted/20 p-3">
              <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Source intake setup</p>
              <p className="mt-1 text-[11px] text-muted-foreground">
                New sources start inactive. Add the first accepted project type here, then use Config for advanced rules and tokens.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Source Name <span className="text-destructive">*</span></Label>
              <Input
                className="h-9 text-xs"
                placeholder="e.g., Google Ads, Angi Leads"
                value={newSource.name || ""}
                onChange={(e) => {
                  const name = e.target.value;
                  setNewSource((p) => ({
                    ...p,
                    name,
                    code: p.code ? p.code : slugifySourceCode(name),
                  }));
                }}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Source Code <span className="text-destructive">*</span></Label>
              <Input
                className="h-9 text-xs font-mono"
                placeholder="e.g., angi_leads"
                value={newSource.code || ""}
                onChange={(e) => setNewSource((p) => ({ ...p, code: slugifySourceCode(e.target.value) }))}
              />
              <p className="text-[10px] text-muted-foreground">
                Providers send this as <span className="font-mono">source_code</span> to the lead intake webhook.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Source Type</Label>
                <Select value={newSource.sourceType || "other"} onValueChange={(v) => setNewSource((p) => ({ ...p, sourceType: v as SourceType }))}>
                  <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(SOURCE_TYPE_CONFIG).map(([key, cfg]) => (
                      <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Campaign / Channel</Label>
                <Input className="h-9 text-xs" placeholder="Optional" value={newSource.campaign || ""} onChange={(e) => setNewSource((p) => ({ ...p, campaign: e.target.value }))} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Source spend budget</Label>
              <div className="grid grid-cols-[130px_1fr] gap-2">
                <Select
                  value={newSource.marketingBudgetPeriod || "monthly"}
                  onValueChange={(value) =>
                    setNewSource((p) => ({ ...p, marketingBudgetPeriod: value as "weekly" | "monthly" }))
                  }
                >
                  <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  className="h-9 text-xs"
                  type="number"
                  min={0}
                  step="0.01"
                  placeholder="No source budget"
                  value={newSource.marketingBudgetAmount || ""}
                  onChange={(e) => setNewSource((p) => ({ ...p, marketingBudgetAmount: e.target.value }))}
                />
              </div>
              <p className="text-[10px] text-muted-foreground">
                Optional planning limit for the whole source. It does not reject leads.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Growth Channel Mapping</Label>
              <Select value={newSource.growthChannelId || "_none"} onValueChange={(v) => setNewSource((p) => ({ ...p, growthChannelId: v === "_none" ? undefined : v }))}>
                <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Select channel..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">None</SelectItem>
                  {GROWTH_CHANNELS.map((ch) => (
                    <SelectItem key={ch.id} value={ch.id}>
                      {ch.name} {ch.type === "managed" ? "· Managed" : ch.type === "coming_soon" ? "· Soon" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {newSource.name && suggestChannel(newSource.name) && !newSource.growthChannelId && (
                <p className="text-[10px] text-primary/70 flex items-center gap-1 mt-0.5">
                  <Link2 className="h-2.5 w-2.5" />
                  Suggested: {getChannelById(suggestChannel(newSource.name)!)?.name}
                </p>
              )}
            </div>
            <div className="rounded-md border bg-background/50 p-3 space-y-3">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Initial accepted project type</p>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Add the first active project type now. You can add more rules from Config after creation.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Project Type <span className="text-destructive">*</span></Label>
                <Input
                  className="h-9 text-xs"
                  placeholder="e.g., Shower Remodel"
                  value={newSourceProjectRule.projectType}
                  onChange={(e) => setNewSourceProjectRule((current) => ({ ...current, projectType: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-semibold">Min project value</Label>
                  <Input
                    className="h-8 text-xs"
                    type="number"
                    min={0}
                    step="0.01"
                    placeholder="No min"
                    value={newSourceProjectRule.minBudget}
                    onChange={(e) => setNewSourceProjectRule((current) => ({ ...current, minBudget: e.target.value }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-semibold">Max project value</Label>
                  <Input
                    className="h-8 text-xs"
                    type="number"
                    min={0}
                    step="0.01"
                    placeholder="No max"
                    value={newSourceProjectRule.maxBudget}
                    onChange={(e) => setNewSourceProjectRule((current) => ({ ...current, maxBudget: e.target.value }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-semibold">Min lead cost</Label>
                  <Input
                    className="h-8 text-xs"
                    type="number"
                    min={0}
                    step="0.01"
                    placeholder="No min"
                    value={newSourceProjectRule.minLeadCost}
                    onChange={(e) => setNewSourceProjectRule((current) => ({ ...current, minLeadCost: e.target.value }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-semibold">Max lead cost</Label>
                  <Input
                    className="h-8 text-xs"
                    type="number"
                    min={0}
                    step="0.01"
                    placeholder="No max"
                    value={newSourceProjectRule.maxLeadCost}
                    onChange={(e) => setNewSourceProjectRule((current) => ({ ...current, maxLeadCost: e.target.value }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-semibold">Spend period</Label>
                  <Select
                    value={newSourceProjectRule.marketingBudgetPeriod}
                    onValueChange={(value) =>
                      setNewSourceProjectRule((current) => ({
                        ...current,
                        marketingBudgetPeriod: value as "weekly" | "monthly",
                      }))
                    }
                  >
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-semibold">Spend budget</Label>
                  <Input
                    className="h-8 text-xs"
                    type="number"
                    min={0}
                    step="0.01"
                    placeholder="No budget"
                    value={newSourceProjectRule.marketingBudgetAmount}
                    onChange={(e) => setNewSourceProjectRule((current) => ({ ...current, marketingBudgetAmount: e.target.value }))}
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] font-semibold">Rule-specific ZIPs</Label>
                <Textarea
                  className="min-h-[58px] text-xs"
                  placeholder="Optional. One ZIP per line or comma-separated."
                  value={newSourceProjectRule.allowedZipCodes}
                  onChange={(e) => setNewSourceProjectRule((current) => ({ ...current, allowedZipCodes: e.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Notes</Label>
              <Textarea className="text-xs min-h-[60px]" placeholder="Notes about this source..." value={newSource.notes || ""} onChange={(e) => setNewSource((p) => ({ ...p, notes: e.target.value }))} />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                className="text-xs"
                disabled={addingSource}
                onClick={() => {
                  resetNewSourceDraft();
                  setShowAdd(false);
                }}
              >
                Cancel
              </Button>
              <Button size="sm" className="text-xs gap-1.5" disabled={addingSource} onClick={handleAdd}>
                <Plus className="h-3.5 w-3.5" /> {addingSource ? "Adding..." : "Add Source"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ═══ Detail Dialog ═══ */}
      <Dialog open={!!selectedSource} onOpenChange={(open) => { if (!open) handleRequestCloseDetail(); }}>
        {selectedSource && (() => {
          const cfg = SOURCE_TYPE_CONFIG[selectedSource.sourceType];
          const TypeIcon = cfg.icon;
          const linkedJobs = jobs.filter((j) => j.marketing?.leadSource === selectedSource.name);
          const linkedIssues = issues.filter((i) => linkedJobs.some((j) => j.id === i.jobId));
          const linkedCredits = credits.filter((c) => linkedJobs.some((j) => j.id === c.jobId));
          return (
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-sm flex items-center gap-2">
                  <TypeIcon className={`h-4 w-4 ${cfg.color}`} />
                  {selectedSource.name}
                  <Badge variant="outline" className="text-[9px] gap-1">
                    {detailMode === "config" ? (
                      <>
                        <Settings2 className="h-2.5 w-2.5" /> Config editor
                      </>
                    ) : (
                      <>
                        <Eye className="h-2.5 w-2.5" /> Source detail
                      </>
                    )}
                  </Badge>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                {detailMode === "view" && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="text-[10px] px-2 py-0.5">{cfg.label}</Badge>
                    <Badge variant="outline" className={`text-[10px] px-2 py-0.5 ${selectedSource.active ? "bg-success/10 text-success border-success/20" : "bg-muted text-muted-foreground border-border"}`}>
                      {selectedSource.active ? "Active" : "Inactive"}
                    </Badge>
                    {selectedSource.campaign && (
                      <Badge variant="secondary" className="text-[10px] px-2 py-0.5">{selectedSource.campaign}</Badge>
                    )}
                    <Button variant="outline" size="sm" className="h-6 text-[10px] ml-auto" onClick={() => toggleActive(selectedSource.id)}>
                      {selectedSource.active ? "Deactivate" : "Activate"}
                    </Button>
                  </div>
                )}

                {/* Webhook / validation config */}
                {detailMode === "config" && (
                <div className="rounded-lg border bg-muted/10 p-3 space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-semibold">Webhook & validation config</p>
                      <p className="text-[10px] text-muted-foreground">
                        Structured settings for lead-intake behavior. Raw JSON is only for developer overrides.
                      </p>
                    </div>
                    {!configEditing ? (
                      <Button
                        size="sm"
                        className="h-7 text-[10px]"
                        disabled={!rawSource || rawSourceLoading}
                        onClick={handleStartConfigEdit}
                      >
                        Edit config
                      </Button>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-[10px]"
                          disabled={configSaving}
                          onClick={handleCancelConfigEdit}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          className="h-7 text-[10px]"
                          disabled={!rawSource || rawSourceLoading || configSaving || !configDirty || Boolean(configError)}
                          onClick={handleSaveSourceConfig}
                        >
                          {configSaving ? "Saving..." : "Save config"}
                        </Button>
                      </div>
                    )}
                  </div>

                  {rawSourceLoading ? (
                    <div className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
                      Loading source config...
                    </div>
                  ) : configError && !rawSource ? (
                    <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive">
                      {configError}
                    </div>
                  ) : rawSource ? (
                    <>
                      <div>
                        <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-semibold">Source intake basics</p>
                        <p className="text-[10px] text-muted-foreground mb-2">
                          These identify the source and apply broad dedupe/service-area controls. Project acceptance belongs in the project-type rules below.
                        </p>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div className="space-y-1.5">
                          <Label className="text-[10px] font-semibold">Source code</Label>
                          <Input className="h-8 text-xs font-mono" value={rawSource.code ?? ""} readOnly />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-[10px] font-semibold">Duplicate window days</Label>
                          <Input
                            className="h-8 text-xs"
                            type="number"
                            min={0}
                            value={configForm.duplicateWindowDays}
                            disabled={!configEditing}
                            onChange={(e) => setConfigFormValue("duplicateWindowDays", e.target.value)}
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-[10px] font-semibold">Source spend budget</Label>
                          <div className="grid grid-cols-[110px_1fr] gap-2">
                            <Select
                              value={configForm.marketingBudgetPeriod}
                              disabled={!configEditing}
                              onValueChange={(value) =>
                                setConfigFormValue("marketingBudgetPeriod", value as "weekly" | "monthly")
                              }
                            >
                              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="weekly">Weekly</SelectItem>
                                <SelectItem value="monthly">Monthly</SelectItem>
                              </SelectContent>
                            </Select>
                            <Input
                              className="h-8 text-xs"
                              type="number"
                              min={0}
                              step="0.01"
                              placeholder="No budget"
                              value={configForm.marketingBudgetAmount}
                              disabled={!configEditing}
                              onChange={(e) => setConfigFormValue("marketingBudgetAmount", e.target.value)}
                            />
                          </div>
                          <p className="text-[10px] text-muted-foreground">
                            Intake spend cap for this source. Leads are blocked when the cap is reached.
                          </p>
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-[10px] font-semibold">Allowed ZIPs</Label>
                          <Textarea
                            className="min-h-[84px] text-xs"
                            placeholder={"One ZIP per line, or comma-separated\n34232\n34293\n33901"}
                            value={configForm.allowedZipCodes}
                            disabled={!configEditing}
                            onChange={(e) => setConfigFormValue("allowedZipCodes", e.target.value)}
                          />
                        </div>
                      </div>

                      <div className="space-y-2 rounded-md border bg-background/50 p-3">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-semibold">Campaign schedule</p>
                            <p className="text-[10px] text-muted-foreground">
                              Optional intake window for this source. Leads submitted outside the schedule are blocked by source policy.
                            </p>
                          </div>
                          <Switch
                            checked={configForm.campaignScheduleEnabled}
                            disabled={!configEditing}
                            className={CONFIG_SWITCH_CLASS}
                            thumbClassName={CONFIG_SWITCH_THUMB_CLASS}
                            onCheckedChange={(checked) => setConfigFormValue("campaignScheduleEnabled", checked)}
                          />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-[1.2fr_0.8fr_0.8fr] gap-2">
                          <div className="space-y-1.5">
                            <Label className="text-[10px] font-semibold">Timezone</Label>
                            <Select
                              value={configForm.campaignScheduleTimezone}
                              disabled={!configEditing || !configForm.campaignScheduleEnabled}
                              onValueChange={(value) => setConfigFormValue("campaignScheduleTimezone", value)}
                            >
                              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {CAMPAIGN_SCHEDULE_TIMEZONES.map((timezone) => (
                                  <SelectItem key={timezone} value={timezone}>{timezone}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1.5">
                            <Label className="text-[10px] font-semibold">Start</Label>
                            <Input
                              className="h-8 text-xs"
                              type="time"
                              value={configForm.campaignScheduleStartTime}
                              disabled={!configEditing || !configForm.campaignScheduleEnabled}
                              onChange={(e) => setConfigFormValue("campaignScheduleStartTime", e.target.value)}
                            />
                          </div>
                          <div className="space-y-1.5">
                            <Label className="text-[10px] font-semibold">End</Label>
                            <Input
                              className="h-8 text-xs"
                              type="time"
                              value={configForm.campaignScheduleEndTime}
                              disabled={!configEditing || !configForm.campaignScheduleEnabled}
                              onChange={(e) => setConfigFormValue("campaignScheduleEndTime", e.target.value)}
                            />
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {CAMPAIGN_SCHEDULE_DAYS.map((day) => {
                            const selected = configForm.campaignScheduleDays.includes(day.key);
                            return (
                              <Button
                                key={day.key}
                                type="button"
                                variant="outline"
                                size="sm"
                                className={`h-7 px-2 text-[10px] ${selected ? "border-primary/40 bg-primary/10 text-primary" : "text-muted-foreground"}`}
                                disabled={!configEditing || !configForm.campaignScheduleEnabled}
                                onClick={() => toggleCampaignScheduleDay(day.key)}
                              >
                                {day.label}
                              </Button>
                            );
                          })}
                        </div>
                      </div>

                      <div className="space-y-2 rounded-md border bg-background/50 p-3">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-semibold">Accepted project types</p>
                            <p className="text-[10px] text-muted-foreground">
                              Keep one source, then accept or pause individual project types with their own budget and lead-cost ranges.
                            </p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 text-[10px] shrink-0"
                            disabled={!configEditing}
                            onClick={handleAddProjectTypeRule}
                          >
                            <Plus className="h-3 w-3 mr-1" /> Add type
                          </Button>
                        </div>

                        {projectTypeRules.length === 0 ? (
                          <div className="rounded-md border border-dashed p-3 text-[10px] text-muted-foreground">
                            No project-type rules yet. Add at least one active rule before using this source for project acceptance.
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {projectTypeRules.map((rule, index) => (
                              <div key={rule.id} className="rounded-md border bg-muted/20 p-2 space-y-2">
                                <div className="flex items-center justify-between gap-3">
                                  <div className="flex min-w-0 flex-1 items-center gap-2">
                                    <Switch
                                      checked={rule.active}
                                      disabled={!configEditing}
                                      className={CONFIG_SWITCH_CLASS}
                                      thumbClassName={CONFIG_SWITCH_THUMB_CLASS}
                                      onCheckedChange={(checked) => handleUpdateProjectTypeRule(index, { active: checked })}
                                    />
                                    <Input
                                      className="h-8 text-xs"
                                      placeholder="Project type, e.g. Shower Remodel"
                                      value={rule.projectType}
                                      disabled={!configEditing}
                                      onChange={(e) => handleUpdateProjectTypeRule(index, { projectType: e.target.value })}
                                    />
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 text-[10px] text-destructive"
                                    disabled={!configEditing}
                                    onClick={() => handleRemoveProjectTypeRule(index)}
                                  >
                                    Remove
                                  </Button>
                                </div>

                                <div className="grid grid-cols-2 gap-2 md:grid-cols-6">
                                  <div className="space-y-1">
                                    <Label className="text-[9px] font-semibold text-muted-foreground">Min project value</Label>
                                    <Input
                                      className="h-7 text-[11px]"
                                      type="number"
                                      min={0}
                                      step="0.01"
                                      placeholder="No min"
                                      value={rule.minBudget}
                                      disabled={!configEditing}
                                      onChange={(e) => handleUpdateProjectTypeRule(index, { minBudget: e.target.value })}
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-[9px] font-semibold text-muted-foreground">Max project value</Label>
                                    <Input
                                      className="h-7 text-[11px]"
                                      type="number"
                                      min={0}
                                      step="0.01"
                                      placeholder="No max"
                                      value={rule.maxBudget}
                                      disabled={!configEditing}
                                      onChange={(e) => handleUpdateProjectTypeRule(index, { maxBudget: e.target.value })}
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-[9px] font-semibold text-muted-foreground">Min lead cost</Label>
                                    <Input
                                      className="h-7 text-[11px]"
                                      type="number"
                                      min={0}
                                      step="0.01"
                                      placeholder="No min"
                                      value={rule.minLeadCost}
                                      disabled={!configEditing}
                                      onChange={(e) => handleUpdateProjectTypeRule(index, { minLeadCost: e.target.value })}
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-[9px] font-semibold text-muted-foreground">Max lead cost</Label>
                                    <Input
                                      className="h-7 text-[11px]"
                                      type="number"
                                      min={0}
                                      step="0.01"
                                      placeholder="No max"
                                      value={rule.maxLeadCost}
                                      disabled={!configEditing}
                                      onChange={(e) => handleUpdateProjectTypeRule(index, { maxLeadCost: e.target.value })}
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-[9px] font-semibold text-muted-foreground">Spend period</Label>
                                    <Select
                                      value={rule.marketingBudgetPeriod}
                                      disabled={!configEditing}
                                      onValueChange={(value) =>
                                        handleUpdateProjectTypeRule(index, { marketingBudgetPeriod: value as "weekly" | "monthly" })
                                      }
                                    >
                                      <SelectTrigger className="h-7 text-[11px]"><SelectValue /></SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="weekly">Weekly</SelectItem>
                                        <SelectItem value="monthly">Monthly</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-[9px] font-semibold text-muted-foreground">Spend budget</Label>
                                    <Input
                                      className="h-7 text-[11px]"
                                      type="number"
                                      min={0}
                                      step="0.01"
                                      placeholder="No budget"
                                      value={rule.marketingBudgetAmount}
                                      disabled={!configEditing}
                                      onChange={(e) => handleUpdateProjectTypeRule(index, { marketingBudgetAmount: e.target.value })}
                                    />
                                  </div>
                                </div>

                                <div className="space-y-1">
                                  <Label className="text-[9px] font-semibold text-muted-foreground">Rule-specific ZIPs</Label>
                                  <Textarea
                                    className="min-h-[58px] text-[11px]"
                                    placeholder="Optional. Leave blank to use source-level allowed ZIPs."
                                    value={rule.allowedZipCodes}
                                    disabled={!configEditing}
                                    onChange={(e) => handleUpdateProjectTypeRule(index, { allowedZipCodes: e.target.value })}
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="space-y-2 rounded-md border bg-background/50 p-3">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-semibold">Webhook access tokens</p>
                            <p className="text-[10px] text-muted-foreground">
                              Provider requests use <span className="font-mono">x-webhook-secret</span>. Scoped tokens take precedence over the primary secret when configured.
                            </p>
                          </div>
                          <div className="shrink-0 space-y-1.5">
                            <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-semibold">New token scopes</p>
                            <ScopeToggleGroup scopes={newScopedTokenScopes} onToggle={handleToggleNewScopedTokenScope} disabled={!configEditing} />
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 text-[10px]"
                              disabled={!configEditing}
                              onClick={handleAddScopedAccessToken}
                            >
                              Add scoped token
                            </Button>
                          </div>
                        </div>

                        <div className="rounded-md border bg-muted/20 p-2">
                          <div className="flex items-center justify-between gap-3 flex-wrap">
                            <div>
                              <p className="text-[11px] font-semibold">Primary webhook secret</p>
                              <p className="font-mono text-[11px] text-muted-foreground">{maskSecret(rawSource.webhook_secret)}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 text-[10px]"
                                disabled={!rawSource.webhook_secret}
                                onClick={() => copySecretToClipboard(rawSource.webhook_secret, "Primary webhook secret")}
                              >
                                Copy
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 text-[10px]"
                                disabled={!configEditing || configSaving}
                                onClick={handleRotatePrimaryWebhookSecret}
                              >
                                Rotate
                              </Button>
                            </div>
                          </div>
                          {scopedAccessTokens.length > 0 && (
                            <p className="mt-2 text-[10px] text-warning">
                              Scoped tokens are configured, so the primary secret is currently ignored by the webhook.
                            </p>
                          )}
                        </div>

                        <div className="space-y-2">
                          {scopedAccessTokens.length === 0 ? (
                            <div className="rounded-md border border-dashed p-2 text-[10px] text-muted-foreground">
                              No scoped tokens configured. The webhook will use the primary secret unless unsigned mode is enabled.
                            </div>
                          ) : (
                            scopedAccessTokens.map((token, index) => {
                              const tokenLabel = token.label ?? token.name ?? token.id ?? `Token ${index + 1}`;
                              const tokenKey = `${token.secret ?? token.id ?? token.name ?? "token"}-${index}`;
                              return (
                                <div key={tokenKey} className="rounded-md border bg-muted/20 p-2">
                                  <div className="flex items-center justify-between gap-3 flex-wrap">
                                    <div>
                                      <div className="space-y-1">
                                        <Label className="text-[9px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Token name</Label>
                                        <Input
                                          className="h-7 max-w-xs text-[11px]"
                                          value={token.label ?? token.name ?? token.id ?? ""}
                                          placeholder={`Token ${index + 1}`}
                                          disabled={!configEditing}
                                          onChange={(e) => handleRenameScopedAccessToken(index, e.target.value)}
                                          onBlur={(e) => {
                                            if (!e.target.value.trim()) handleRenameScopedAccessToken(index, `Token ${index + 1}`);
                                          }}
                                        />
                                      </div>
                                      <p className="font-mono text-[11px] text-muted-foreground">{maskSecret(token.secret)}</p>
                                      <div className="mt-2 space-y-1">
                                        <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-semibold">Scopes</p>
                                        <ScopeToggleGroup
                                          scopes={token.scopes}
                                          onToggle={(scope) => handleToggleScopedAccessTokenScope(index, scope)}
                                          disabled={!configEditing}
                                        />
                                      </div>
                                      {!normalizeTokenScopes(token.scopes).includes("intake.submit") && (
                                        <p className="mt-1 text-[10px] text-warning">
                                          This token cannot submit leads unless Submit is selected.
                                        </p>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="h-7 text-[10px]"
                                        disabled={!token.secret}
                                        onClick={() => copySecretToClipboard(token.secret, tokenLabel)}
                                      >
                                        Copy
                                      </Button>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="h-7 text-[10px] text-destructive"
                                        disabled={!configEditing}
                                        onClick={() => handleRemoveScopedAccessToken(index)}
                                      >
                                        Remove
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div>
                          <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-semibold">Payload gates</p>
                          <p className="text-[10px] text-muted-foreground">
                            These decide whether a submitted lead is complete enough to enter the pipeline.
                          </p>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {[
                          {
                            label: "Require TCPA consent",
                            checked: configForm.requireTcpa,
                            onChange: (checked: boolean) => setConfigFormValue("requireTcpa", checked),
                          },
                          {
                            label: "Allow unsigned webhook",
                            checked: getNestedConfigValue(parsedConfig, ["webhook", "allow_unsigned"]) === true ||
                              getNestedConfigValue(parsedConfig, ["allow_unsigned_webhook"]) === true,
                            onChange: (checked: boolean) =>
                              updateConfigDraftMany([
                                { path: ["webhook", "allow_unsigned"], value: checked },
                                { path: ["allow_unsigned_webhook"], value: checked },
                              ]),
                          },
                          {
                            label: "Require identity signal",
                            checked: getNestedConfigValue(parsedConfig, ["payload_validation", "require_identity"]) !== false,
                            onChange: (checked: boolean) => updateConfigDraft(["payload_validation", "require_identity"], checked),
                          },
                          {
                            label: "Require phone",
                            checked: payloadContactRequirements.requirePhone,
                            onChange: (checked: boolean) =>
                              updateConfigDraftMany(
                                buildPayloadContactRequirementUpdates(
                                  payloadContactRequirements,
                                  "phone",
                                  checked,
                                ),
                              ),
                          },
                          {
                            label: "Require email",
                            checked: payloadContactRequirements.requireEmail,
                            onChange: (checked: boolean) =>
                              updateConfigDraftMany(
                                buildPayloadContactRequirementUpdates(
                                  payloadContactRequirements,
                                  "email",
                                  checked,
                                ),
                              ),
                          },
                          {
                            label: "Require phone for booking",
                            checked: getNestedConfigValue(parsedConfig, ["payload_validation", "require_phone_for_booking"]) === true,
                            onChange: (checked: boolean) => updateConfigDraft(["payload_validation", "require_phone_for_booking"], checked),
                          },
                        ].map((control) => (
                          <label key={control.label} className="flex items-center justify-between gap-3 rounded-md border bg-background/50 px-3 py-2">
                            <span className="text-[11px] font-medium">{control.label}</span>
                            <Switch
                              checked={control.checked}
                              disabled={!configEditing}
                              className={CONFIG_SWITCH_CLASS}
                              thumbClassName={CONFIG_SWITCH_THUMB_CLASS}
                              onCheckedChange={control.onChange}
                            />
                          </label>
                        ))}
                        </div>
                        {payloadContactRequirements.legacyRequirePhoneOrEmail && (
                          <p className="text-[10px] text-muted-foreground">
                            This source still uses the legacy rule requiring at least one phone number or email address.
                            Enabling either separate requirement replaces that rule.
                          </p>
                        )}
                        <div className="space-y-2 rounded-md border bg-background/30 p-3">
                          <div>
                            <p className="text-[10px] font-semibold">Required mapped fields</p>
                            <p className="text-[9px] text-muted-foreground">
                              Selected fields are checked after mapping and normalization. Missing values reject the lead with a field-specific reason.
                            </p>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                            {REQUIRED_PAYLOAD_FIELD_OPTIONS.map((field) => (
                              <label
                                key={field.key}
                                className="flex items-center justify-between gap-2 rounded-md border bg-background/50 px-2.5 py-2"
                              >
                                <span className="text-[10px] font-medium">{field.label}</span>
                                <Switch
                                  checked={requiredPayloadFields.includes(field.key)}
                                  disabled={!configEditing}
                                  className={CONFIG_SWITCH_CLASS}
                                  thumbClassName={CONFIG_SWITCH_THUMB_CLASS}
                                  onCheckedChange={(checked) =>
                                    updateConfigDraft(
                                      ["payload_validation", "required_fields"],
                                      toggleRequiredPayloadField(
                                        requiredPayloadFields,
                                        field.key,
                                        checked,
                                      ),
                                    )}
                                />
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-semibold">Validation services</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {[
                            {
                              key: "phone_check" as const,
                              label: "Phone blacklist checking",
                              description:
                                "Checks whether a phone is listed by Blacklist Alliance and determines call and SMS eligibility.",
                              settings: blacklistAllianceConfig.phoneCheck,
                              activationUnavailable: false,
                            },
                            {
                              key: "email_filter" as const,
                              label: "Email suppression filtering",
                              description: BLACKLIST_EMAIL_FILTER_UNAVAILABLE_EXPLANATION,
                              settings: blacklistAllianceConfig.emailFilter,
                              activationUnavailable: true,
                            },
                          ].map((check) => (
                            <div key={check.key} className="rounded-md border bg-background/50 p-2 space-y-2">
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <p className="text-[11px] font-semibold">{check.label}</p>
                                  <p className="mt-0.5 text-[9px] leading-relaxed text-muted-foreground">
                                    {check.description}
                                  </p>
                                  {check.activationUnavailable && (
                                    <p className="mt-1 text-[9px] font-semibold text-warning">
                                      {BLACKLIST_EMAIL_FILTER_UNAVAILABLE_STATUS}
                                    </p>
                                  )}
                                </div>
                                <Switch
                                  checked={check.settings.enabled}
                                  disabled={!configEditing ||
                                    (check.activationUnavailable && !check.settings.enabled)}
                                  className={CONFIG_SWITCH_CLASS}
                                  thumbClassName={CONFIG_SWITCH_THUMB_CLASS}
                                  onCheckedChange={(enabled) =>
                                    updateBlacklistAllianceDraft(check.key, { enabled })}
                                />
                              </div>
                              <label className="flex items-center justify-between gap-3">
                                <span className="text-[10px] text-muted-foreground">Run on intake</span>
                                <Switch
                                  checked={check.settings.intake}
                                  disabled={!configEditing}
                                  className={CONFIG_SWITCH_CLASS}
                                  thumbClassName={CONFIG_SWITCH_THUMB_CLASS}
                                  onCheckedChange={(intake) =>
                                    updateBlacklistAllianceDraft(check.key, { intake })}
                                  />
                              </label>
                              {check.activationUnavailable && check.settings.enabled && (
                                <p role="alert" className="text-[9px] leading-relaxed text-destructive">
                                  {BLACKLIST_EMAIL_FILTER_ENABLED_WARNING}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {VALIDATION_SERVICE_CONTROLS.map((service) => {
                            const enabled = getNestedConfigValue(parsedConfig, ["services", service.key, "enabled"]) === true;
                            const intake = getNestedConfigValue(parsedConfig, ["services", service.key, "intake"]) !== false;
                            return (
                              <div key={service.key} className="rounded-md border bg-background/50 p-2 space-y-2">
                                <div className="flex items-center justify-between gap-3">
                                  <span className="text-[11px] font-semibold">{service.label}</span>
                                  <Switch
                                    checked={enabled}
                                    disabled={!configEditing}
                                    className={CONFIG_SWITCH_CLASS}
                                    thumbClassName={CONFIG_SWITCH_THUMB_CLASS}
                                    onCheckedChange={(checked) => updateConfigDraft(["services", service.key, "enabled"], checked)}
                                  />
                                </div>
                                <label className="flex items-center justify-between gap-3">
                                  <span className="text-[10px] text-muted-foreground">Run on intake</span>
                                  <Switch
                                    checked={intake}
                                    disabled={!configEditing}
                                    className={CONFIG_SWITCH_CLASS}
                                    thumbClassName={CONFIG_SWITCH_THUMB_CLASS}
                                    onCheckedChange={(checked) => updateConfigDraft(["services", service.key, "intake"], checked)}
                                  />
                                </label>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div>
                          <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-semibold">Abuse protection & rate limits</p>
                          <p className="text-[10px] text-muted-foreground">
                            Protects the webhook from oversized payloads, repeated submissions, and noisy provider retries.
                          </p>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {[
                            {
                              label: "Enable abuse protection",
                              checked: getNestedConfigValue(parsedConfig, ["abuse_protection", "enabled"]) !== false,
                              onChange: (checked: boolean) => updateConfigDraft(["abuse_protection", "enabled"], checked),
                            },
                            {
                              label: "Require requester IP",
                              checked: getNestedConfigValue(parsedConfig, ["abuse_protection", "require_requester_ip"]) === true,
                              onChange: (checked: boolean) => updateConfigDraft(["abuse_protection", "require_requester_ip"], checked),
                            },
                          ].map((control) => (
                          <label key={control.label} className="flex items-center justify-between gap-3 rounded-md border bg-background/50 px-3 py-2">
                            <span className="text-[11px] font-medium">{control.label}</span>
                              <Switch
                                checked={control.checked}
                                disabled={!configEditing}
                                className={CONFIG_SWITCH_CLASS}
                                thumbClassName={CONFIG_SWITCH_THUMB_CLASS}
                                onCheckedChange={control.onChange}
                              />
                            </label>
                          ))}
                          {ABUSE_PROTECTION_NUMBER_CONTROLS.map((control) => (
                            <div key={control.path.join(".")} className="space-y-1.5">
                              <Label className="text-[10px] font-semibold">{control.label}</Label>
                              <Input
                                className="h-8 text-xs"
                                type="number"
                                min={control.min}
                                step={control.step}
                                value={configNumberValue(parsedConfig, [...control.path], control.fallback)}
                                disabled={!configEditing}
                                onChange={(e) => updateConfigDraft([...control.path], Number(e.target.value))}
                              />
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="rounded-md border bg-background/50 p-3">
                        <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-semibold">What should live here?</p>
                        <div className="mt-2 grid gap-1 text-[10px] text-muted-foreground sm:grid-cols-2">
                          <p><span className="font-semibold text-foreground">Keep editable:</span> dedupe, TCPA, defaults, ZIPs, payload gates, validation toggles, and rate limits.</p>
                          <p><span className="font-semibold text-foreground">Move out:</span> response tokens, raw provider secrets, one-off experimental flags, and vendor settings better managed by migration/Supabase secrets.</p>
                        </div>
                      </div>

                      <details className="rounded-md border border-amber-500/20 bg-amber-500/5 p-3">
                        <summary className="cursor-pointer text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                          Developer JSON override
                          {configDirty && <Badge variant="outline" className="ml-2 text-[9px] text-warning border-warning/30 normal-case tracking-normal">Unsaved</Badge>}
                        </summary>
                        <div className="mt-3 space-y-1.5">
                          <Textarea
                            className="min-h-[180px] font-mono text-[11px]"
                            value={configText}
                            disabled={!configEditing}
                            onChange={(e) => handleConfigTextChange(e.target.value)}
                            spellCheck={false}
                          />
                          {configError ? (
                            <p className="text-[10px] text-destructive">{configError}</p>
                          ) : (
                            <p className="text-[10px] text-muted-foreground">
                              Saved to <span className="font-mono">public.lead_sources.config</span>. Use only for exceptional flags not represented above.
                            </p>
                          )}
                        </div>
                      </details>
                    </>
                  ) : null}
                </div>
                )}

                {detailMode === "view" && (
                <>
                {/* Funnel */}
                <div>
                  <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-semibold mb-2">Lead Funnel</p>
                  <div className="space-y-1.5">
                    {[
                      { label: "Leads", value: selectedSource.totalLeads, rate: null },
                      { label: "Contacted", value: selectedSource.contacted, rate: selectedSource.connectionRate },
                      { label: "Appts Set", value: selectedSource.appointmentsSet, rate: selectedSource.bookingRate },
                      { label: "Appts Completed", value: selectedSource.appointmentsCompleted, rate: selectedSource.showRate },
                      { label: "Sold", value: selectedSource.closed, rate: selectedSource.saleRate },
                      { label: "Installed", value: selectedSource.installed, rate: null },
                    ].map((step, idx) => (
                      <div key={step.label} className="flex items-center gap-2">
                        <span className="w-32 text-[11px] font-medium text-muted-foreground">{step.label}</span>
                        <div className="flex-1">
                          <Progress value={selectedSource.totalLeads > 0 ? (step.value / selectedSource.totalLeads) * 100 : 0} className="h-1.5" />
                        </div>
                        <span className="w-10 text-right text-xs font-bold">{step.value}</span>
                        {step.rate !== null && (
                          <span className={`w-12 text-right text-[10px] font-medium ${step.rate >= 70 ? "text-success" : step.rate >= 40 ? "text-warning" : "text-muted-foreground"}`}>
                            {step.rate}%
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Ranking + Recommendation */}
                <div className="rounded-lg border p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={`text-[10px] px-2 py-0.5 ${selectedSource.ranking.badgeClass}`}>
                        {selectedSource.ranking.label}
                      </Badge>
                      <span className="text-[10px] text-muted-foreground">Net Conv: {selectedSource.netConversionPct}% · Quality: {selectedSource.leadQualityPct}%</span>
                    </div>
                  </div>
                  <div className={`rounded-md border px-3 py-2 flex items-start gap-2 ${
                    selectedSource.budgetRec.severity === "positive" ? "bg-success/5 border-success/15" :
                    selectedSource.budgetRec.severity === "warning" ? "bg-warning/5 border-warning/15" :
                    "bg-muted/30 border-border"
                  }`}>
                    {selectedSource.budgetRec.icon === "increase" ? <TrendingUp className="h-3.5 w-3.5 text-success mt-0.5 shrink-0" /> :
                     selectedSource.budgetRec.icon === "reduce" ? <AlertTriangle className="h-3.5 w-3.5 text-warning mt-0.5 shrink-0" /> :
                     <Eye className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />}
                    <div>
                      <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-0.5">System Recommendation</p>
                      <p className="text-[12px] font-medium">{selectedSource.budgetRec.message}</p>
                    </div>
                  </div>
                </div>

                {/* Performance + Cost summary */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[
                    { label: "Revenue", value: `$${selectedSource.revenue.toLocaleString()}`, color: "text-success" },
                    { label: "Avg Value", value: `$${selectedSource.avgValue.toLocaleString()}`, color: "text-primary" },
                    { label: "CPA", value: selectedSource.cpa > 0 ? `$${selectedSource.cpa}` : "—", color: "text-warning" },
                    { label: "ROI", value: selectedSource.totalCost > 0 ? `${selectedSource.roi}%` : "—", color: selectedSource.roi > 200 ? "text-success" : "text-warning" },
                  ].map((m) => (
                    <div key={m.label} className="rounded-lg border p-2.5 bg-muted/20 text-center">
                      <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-semibold">{m.label}</p>
                      <p className={`text-sm font-bold ${m.color}`}>{m.value}</p>
                    </div>
                  ))}
                </div>

                {/* Operational Impact */}
                <div className="grid grid-cols-3 gap-2">
                  <div className="rounded-lg border p-2.5 bg-muted/20 text-center">
                    <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-semibold">Issues</p>
                    <p className={`text-sm font-bold ${selectedSource.issueCount > 0 ? "text-warning" : "text-success"}`}>{selectedSource.issueCount}</p>
                    <p className="text-[9px] text-muted-foreground">{selectedSource.issueRate}/job</p>
                  </div>
                  <div className="rounded-lg border p-2.5 bg-muted/20 text-center">
                    <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-semibold">Credits</p>
                    <p className={`text-sm font-bold ${selectedSource.creditAmount > 0 ? "text-warning" : "text-success"}`}>${selectedSource.creditAmount.toLocaleString()}</p>
                    <p className="text-[9px] text-muted-foreground">{selectedSource.creditCount} records</p>
                  </div>
                  <div className="rounded-lg border p-2.5 bg-muted/20 text-center">
                    <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-semibold">Avg Credit/Job</p>
                    <p className="text-sm font-bold text-muted-foreground">${selectedSource.avgCredit.toLocaleString()}</p>
                  </div>
                </div>

                {selectedSource.notes && (
                  <div className="rounded-lg border bg-muted/20 p-3">
                    <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-semibold mb-1">Notes</p>
                    <p className="text-xs text-muted-foreground">{selectedSource.notes}</p>
                  </div>
                )}

                {/* Linked Jobs */}
                {linkedJobs.length > 0 && (
                  <div>
                    <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-semibold mb-2 flex items-center gap-1.5">
                      <Link2 className="h-3 w-3" /> Linked Jobs ({linkedJobs.length})
                    </p>
                    <div className="space-y-1 max-h-[160px] overflow-y-auto">
                      {linkedJobs.slice(0, 10).map((j) => (
                        <Link key={j.id} to={`/jobs/${j.id}`} className="flex items-center justify-between rounded-lg border p-2 hover:bg-muted/40 transition-colors">
                          <div className="min-w-0">
                            <p className="text-[11px] font-semibold">{j.id} — {j.customerName}</p>
                            <p className="text-[10px] text-muted-foreground">{j.projectType} · {j.currentStage}</p>
                          </div>
                          <span className="text-xs font-medium text-primary shrink-0">
                            ${getJobDisplayValue(j).toLocaleString()}
                          </span>
                        </Link>
                      ))}
                      {linkedJobs.length > 10 && (
                        <p className="text-[10px] text-muted-foreground text-center pt-1">+ {linkedJobs.length - 10} more</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Linked Issues */}
                {linkedIssues.length > 0 && (
                  <div>
                    <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-semibold mb-2 flex items-center gap-1.5">
                      <AlertTriangle className="h-3 w-3" /> Issues ({linkedIssues.length})
                    </p>
                    <div className="space-y-1 max-h-[120px] overflow-y-auto">
                      {linkedIssues.slice(0, 5).map((i) => (
                        <div key={i.id} className="flex items-center justify-between rounded-lg border p-2">
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] font-mono text-muted-foreground">{i.jobId}</span>
                            <span className="text-xs font-medium">{i.issueType}</span>
                          </div>
                          <StatusBadge type="severity" value={i.severity} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Linked Credits */}
                {linkedCredits.length > 0 && (
                  <div>
                    <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-semibold mb-2 flex items-center gap-1.5">
                      <Receipt className="h-3 w-3" /> Credits ({linkedCredits.length})
                    </p>
                    <div className="space-y-1 max-h-[120px] overflow-y-auto">
                      {linkedCredits.slice(0, 5).map((c) => (
                        <div key={c.id} className="flex items-center justify-between rounded-lg border p-2">
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] font-mono text-muted-foreground">{c.jobId}</span>
                            <span className="text-xs font-medium">{c.creditType}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-primary">${c.creditAmount.toLocaleString()}</span>
                            <StatusBadge type="creditStatus" value={c.status} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                </>
                )}
              </div>
            </DialogContent>
          );
        })()}
      </Dialog>
    </div>
  );
}
